/**
 * A Special type of stack that we can predefine its maximum size.
 * If we didn't pass the size to the constructor, it will behave as the normal stack.
 * It supports all stack operations.
 * The only difference is when specifying the max size.
 * If you tried to add more elements using the "push" method after reaching the max size you defined, it will
 * push normally but it will also remove the first added element to keep the size fixed.
 * There is a special method called "pushIfAvailable". It pushes the element only if the stack is not full, otherwise it does nothing.
 */
export default class SizedStack<T> {
  _maxSize: number;
  _stack: T[];

  constructor(size?: number) {
    this._maxSize = size === undefined ? Infinity : size;
    this._stack = [];
  }

  _insertOneItem(item: T) {
    this._stack.push(item);
  }

  _shiftAndInsert(item: T) {
    this._stack.shift();
    this._insertOneItem(item);
  }

  push(...items: T[]) {
    items.forEach((item) => {
      this.isFull ? this._shiftAndInsert(item) : this._insertOneItem(item);
    });
  }

  pushIfAvailable(...items: T[]) {
    items.forEach((item) => {
      if (!this.isFull) this._insertOneItem(item);
    });
  }

  pop() {
    this._stack.pop();
  }

  get top() {
    return this._stack.at(-1);
  }

  get isFull() {
    return this._stack.length === this._maxSize;
  }

  get isEmpty() {
    return this._stack.length === 0;
  }

  get size() {
    return this._stack.length;
  }

  get asList() {
    return [...this._stack];
  }
}
